import type { FormEvent } from 'react'
import React, { useState } from 'react'
import {
  Button,
  FormField,
  InputV2,
  Link,
  Picture,
  TypographyV2 as Typography,
} from '@which/seatbelt'
import type { ImageWithSources } from '@which/shared'

import { useFeatureIsOn } from '@growthbook/growthbook-react'

import styles from './BroadbandSwitchCTA.module.scss'
import { defaultConfig, march2026Config } from './constants'
import type { BroadbandSwitchCtaConfig } from './types/BroadbandSwitchCtaConfig'

interface Props {
  title?: string
  description?: string
  offerText?: string
  smallPrint?: { text: string; link: string }
  image?: ImageWithSources
}

export const BroadbandSwitchCTA: React.FC<Props> = ({
  title = defaultConfig.title,
  description = defaultConfig.description,
  image = defaultConfig.image,
}) => {
  const isMarch2026Offer = useFeatureIsOn('WHC-1407-march-2026-offer')
  const [postcode, setPostcode] = useState<string>('')

  const activeConfig: BroadbandSwitchCtaConfig = isMarch2026Offer
    ? march2026Config
    : {
        ...defaultConfig,
        title,
        description,
        image,
      }

  const link = `https://broadband.which.co.uk/broadband-deals?postcode=${postcode}&utm_source=which&utm_medium=advice&utm_campaign=${activeConfig.campaign}`

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    window.open(link, '_self')
  }

  return (
    <aside aria-labelledby="broadband-cta-heading" className={styles.container}>
      <div className={styles.content}>
        <Typography
          className={styles.heading}
          id="broadband-cta-heading"
          textStyle="sb-text-heading-small"
        >
          {activeConfig.title}
        </Typography>
        <Typography textStyle="sb-text-body-default-regular">{activeConfig.description}</Typography>
        {activeConfig.offerText && (
          <Typography data-testid="offer-text" textStyle="sb-text-body-default-strong">
            {activeConfig.offerText}
          </Typography>
        )}
        <form
          className={styles.form}
          data-which-id="compare-broadband-widget-form"
          name="broadband-switch"
          onSubmit={handleSubmit}
          title={activeConfig.title}
        >
          <FormField
            label="Postcode"
            labelFor="broadband-switch-postcode"
            showRequiredText={false}
            className={styles.postcodeField}
          >
            <InputV2
              id="broadband-switch-postcode"
              name="broadband-switch-postcode"
              placeholder="eg. NW1 4DF"
              value={postcode}
              onChange={(event) => setPostcode(event.target.value)}
            />
          </FormField>
          <Button type="submit">{activeConfig.ctaText}</Button>
          {activeConfig.smallPrint && (
            <Typography
              className={styles.smallPrint}
              data-testid="small-print-terms-conditions"
              textStyle="sb-text-body-x-small-regular"
            >
              {activeConfig.smallPrint.text}
            </Typography>
          )}
        </form>
      </div>
      {activeConfig.image && (
        <Link href={link} className={styles.imageWrapper}>
          <Picture
            src={activeConfig.image.src}
            alt={activeConfig.image.alt}
            sources={activeConfig.image.sources}
            imageClassName={styles.brandImageBg}
          />
        </Link>
      )}
    </aside>
  )
}
